import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { PALETTE_LAYER, useDialogLayer } from '@/shared/hooks/useDialogLayer';
import { useOutcomeRequest } from '@/shared/stores/outcome-request.store';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAskStore } from '@/shared/stores/ask.store';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Icons } from '@/shared/constants/icons';
import { ROUTES } from '@/shared/constants/routes';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';
import { useConversationData } from '@/shared/hooks/useConversationData';
import { usePosts } from '@/shared/hooks/usePosts';
import { useOccasions } from '@/shared/hooks/useOccasions';
import { useAttentionItems } from '@/shared/hooks/useAttention';
import { useAuth } from '@/shared/hooks/useAuth';
import { formatDateTime } from '@/shared/lib/intl';
import { PlatformMark } from './PlatformMark';
import type { PlatformId } from './PlatformIcon';
import { GROUP_ORDER, BROWSE_MAX, SEARCH_MAX, OCCASION_HORIZON_DAYS, readRecent, writeRecent, BRAND_FIELDS, SETTINGS_TABS, PAGES, preview, matches, openBrandSwitcher } from './command-palette/palette-data';
import type { Group, PaletteItem, RecentEntry } from './command-palette/palette-data';
export { RECENT_KEY } from './command-palette/palette-data';

/* ─────────────────────────────────────────────────────────────────────── */
/*  Command palette (blueprint M21) — Ctrl/⌘ K: jump, search, act          */
/* ─────────────────────────────────────────────────────────────────────── */

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onCompose: () => void;
}

export function CommandPalette({ open, onClose, onCompose }: CommandPaletteProps): ReactElement {
  const reduced = useReducedMotion() ?? false;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? undefined : { opacity: 0 }}
          transition={motionSafe(TRANSITION.micro, reduced)}
          className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4"
          style={{ background: 'var(--scrim)' }}
          onClick={onClose}
          role="presentation"
        >
          <PaletteBody onClose={onClose} onCompose={onCompose} reduced={reduced} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface PaletteBodyProps {
  onClose: () => void;
  onCompose: () => void;
  reduced: boolean;
}

function PaletteBody({ onClose, onCompose, reduced }: PaletteBodyProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // A dialog layer: Escape closes it (and only it), focus stays inside and
  // goes back to where it was when the palette closes.
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogLayer(true, onClose, dialogRef, PALETTE_LAYER);
  const [recent] = useState<RecentEntry[]>(() => readRecent());

  const conversations = useConversationData();
  const posts = usePosts({ limit: 20 });
  const range = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + OCCASION_HORIZON_DAYS);
    return { from, to };
  }, []);
  const { upcoming } = useOccasions(range);
  const attention = useAttentionItems();
  const { signOut } = useAuth();

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const failedCount = attention.items.filter((i) => i.kind === 'failed-posts').length;
  const searching = query.trim() !== '';
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  // The local date: the UTC one read "in 1 day" for today between local and
  // UTC midnight (00:00-05:30 in India).
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const go = (to: string): void => { void navigate(to); };

  const all = useMemo<PaletteItem[]>(() => {
    const items: PaletteItem[] = [];

    // Ask UniPostudio (UI v2): the typed words as a question, or the panel itself.
    const asked = query.trim();
    items.push({
      id: 'ask',
      group: 'ask',
      label: asked ? t('palette.askQuery', { query: asked }) : t('palette.askOpen'),
      icon: Icons.sparkles,
      keywords: asked,
      run: () => useAskStore.getState().openWith(asked || null),
    });

    for (const r of recent) {
      items.push({ id: `recent-${r.id}`, group: 'recent', label: r.label, detail: r.detail, platform: r.platform, emoji: r.emoji, icon: Icons.clock, run: () => go(r.to) });
    }

    for (const c of conversations.all) {
      const to = `${ROUTES.INBOX}?conversation=${encodeURIComponent(c.key)}`;
      items.push({ id: `conv-${c.key}`, group: 'conversations', label: c.peer, detail: preview(c.preview), platform: c.platform, keywords: c.handle, run: () => go(to), to });
    }

    for (const p of posts.data?.data ?? []) {
      const status = String(p.status).toLowerCase();
      const statusLabel = status === 'pending_approval' ? t('palette.post.pending') : t(`palette.post.${status}`, { defaultValue: status });
      const detail = p.scheduledAt ? `${statusLabel} · ${formatDateTime(p.scheduledAt)}` : statusLabel;
      const to = `${ROUTES.COMPOSER}/${p.id}`;
      const first = p.platforms[0];
      const platform = first ? (first.toLowerCase() as PlatformId) : undefined;
      items.push({ id: `post-${p.id}`, group: 'posts', label: preview(p.content) || t('palette.post.untitled'), detail, platform, icon: Icons.posts, run: () => go(to), to });
    }

    for (const o of upcoming) {
      const days = Math.round((Date.parse(`${o.date.slice(0, 10)}T00:00:00Z`) - Date.parse(`${todayKey}T00:00:00Z`)) / 86_400_000);
      const to = `${ROUTES.CALENDAR}?occasion=${encodeURIComponent(o.id)}`;
      items.push({ id: `occ-${o.id}`, group: 'occasions', label: o.name, detail: days <= 0 ? t('palette.occasion.today') : t('palette.occasion.in', { count: days }), emoji: o.emoji, run: () => go(to), to });
    }

    items.push({ id: 'act-compose', group: 'actions', label: t('palette.actions.compose'), icon: Icons.add, keywords: 'new post write', run: onCompose });
    if (failedCount > 0) {
      items.push({ id: 'act-retry', group: 'actions', label: t('palette.actions.retryFailedCount', { count: failedCount }), icon: Icons.refresh, keywords: 'retry failed publish attention', run: () => go(`${ROUTES.OVERVIEW}#attention`) });
    }
    items.push({ id: 'act-brand', group: 'actions', label: t('palette.actions.switchBrand'), icon: Icons.building, shortcut: 'Ctrl B', keywords: 'brand workspace switch', run: openBrandSwitcher });
    // In a conversation: mark what it led to (Task 3.2), the inbox's `o`.
    if (pathname.startsWith(ROUTES.INBOX)) {
      items.push({ id: 'act-outcome', group: 'actions', label: t('palette.actions.markOutcome'), icon: Icons.check, shortcut: 'O', keywords: 'outcome sale booking order lost enquiry', run: () => useOutcomeRequest.getState().setOpen(true) });
    }
    items.push({ id: 'act-plan', group: 'actions', label: t('palette.actions.planWeek'), icon: Icons.plan, keywords: 'plan week schedule ideas', run: () => go(ROUTES.PLAN) });
    items.push({ id: 'act-connect', group: 'actions', label: t('palette.actions.connect'), icon: Icons.channels, keywords: 'connect channel account', run: () => go(ROUTES.CHANNELS) });
    items.push({ id: 'act-template', group: 'actions', label: t('palette.actions.fromTemplate'), icon: Icons.templates, keywords: 'template', run: () => go(ROUTES.TEMPLATES) });
    // Offered when searched for, never in the browse list: a stray Enter must not sign anyone out.
    if (searching) {
      items.push({ id: 'act-sign-out', group: 'actions', label: t('topbar.logOut'), icon: Icons.logout, keywords: 'sign out signout log out logout exit leave', run: () => { void signOut().then(() => go(ROUTES.AUTH)); } });
    }

    for (const page of PAGES) {
      if (page.to === pathname) continue;
      items.push({ id: `page-${page.key}`, group: 'pages', label: t(`nav.${page.key}`), icon: page.icon, keywords: page.keywords, run: () => go(page.to) });
    }

    for (const s of SETTINGS_TABS) {
      const to = `${ROUTES.SETTINGS}?tab=${s.tab}`;
      // The palette's own word and the page's are both searched ("Subscription", "Billing and plan").
      const own = t(`palette.settings.${s.key}`);
      const label = s.label ? t(s.label) : own;
      items.push({ id: `settings-${s.key}`, group: 'settings', label, icon: Icons.settings, keywords: `settings ${own} ${s.keywords ?? ''}`, run: () => go(to) });
    }
    items.push({ id: 'settings-inbox', group: 'settings', label: t('palette.settings.inbox'), icon: Icons.inbox, keywords: 'settings escalation ladder knowledge', run: () => go(ROUTES.INBOX_SETTINGS) });
    items.push({ id: 'settings-brand', group: 'settings', label: t('palette.settings.brand'), icon: Icons.building, keywords: 'settings brand profile', run: () => go(ROUTES.BRAND) });
    items.push({ id: 'settings-occasions', group: 'settings', label: t('palette.settings.occasions'), icon: Icons.calendar, keywords: 'settings occasions holidays', run: () => go(ROUTES.CALENDAR) });
    if (searching) {
      for (const f of BRAND_FIELDS) {
        const to = `${ROUTES.BRAND}#brand-section-${f.section}`;
        const field = t(`palette.brand.fields.${f.key}`);
        items.push({ id: `brand-${f.key}`, group: 'settings', label: t('palette.brand.prefix', { field }), icon: Icons.building, keywords: f.keywords, run: () => go(to), to });
      }
    }
    return items;
    // navigate and t are stable for the life of the palette.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recent, conversations.all, posts.data, upcoming, failedCount, pathname, searching, todayKey, onCompose, signOut, query]);

  const filtered = useMemo(() => {
    const perGroup = new Map<Group, PaletteItem[]>();
    const cap = searching ? SEARCH_MAX : BROWSE_MAX;
    for (const item of all) {
      if (!matches(item, tokens)) continue;
      if (!searching && item.group === 'settings' && item.id.startsWith('brand-')) continue;
      const list = perGroup.get(item.group) ?? [];
      const limit = item.group === 'actions' || item.group === 'pages' || item.group === 'settings' ? (searching ? SEARCH_MAX : 99) : cap;
      if (list.length >= limit) continue;
      list.push(item);
      perGroup.set(item.group, list);
    }
    const flat: PaletteItem[] = [];
    const grouped: [Group, PaletteItem[]][] = [];
    for (const g of GROUP_ORDER) {
      const list = perGroup.get(g);
      if (list && list.length > 0) { grouped.push([g, list]); flat.push(...list); }
    }
    return { flat, grouped };
  }, [all, tokens, searching]);

  useEffect(() => {
    if (activeIndex >= filtered.flat.length) setActiveIndex(Math.max(0, filtered.flat.length - 1));
  }, [filtered.flat.length, activeIndex]);

  const choose = (item: PaletteItem): void => {
    if (item.to) writeRecent({ id: item.id, label: item.label, detail: item.detail, to: item.to, platform: item.platform, emoji: item.emoji });
    inputRef.current?.blur();
    onClose();
    item.run();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      // Escape is the dialog layer's (useDialogLayer): top-most overlay only.
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(filtered.flat.length - 1, i + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(0, i - 1)); }
      else if (e.key === 'Enter') {
        // Nothing to open: the key does nothing, and the palette keeps focus.
        e.preventDefault();
        const item = filtered.flat[activeIndex];
        if (item) choose(item);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // choose closes over the latest filtered list through activeIndex.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.flat, activeIndex, onClose]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-idx="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  let runningIndex = 0;
  const kbd = 'inline-flex items-center px-1.5 py-0.5 rounded border border-[var(--line-2)] bg-[var(--color-surface-2)] text-[var(--ink-3)] text-caption font-mono shrink-0';

  return (
    <motion.div
      ref={dialogRef}
      initial={reduced ? false : { opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduced ? undefined : { opacity: 0, y: -8, scale: 0.98 }}
      transition={motionSafe(TRANSITION.panel, reduced)}
      onClick={(e) => e.stopPropagation()}
      className="card p-0 overflow-hidden shadow-pop w-full max-w-[600px] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={t('palette.label')}
    >
      <div className="field-ring flex items-center gap-2 ps-4 pe-2 h-[52px] border-b border-[var(--line)]">
        <Icons.search size={16} className="text-[var(--ink-3)] shrink-0" aria-hidden="true" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
          placeholder={t('palette.placeholder')}
          className="flex-1 bg-transparent border-none outline-none text-body text-[var(--ink)] placeholder:text-[var(--ink-3)]"
          aria-label={t('palette.searchLabel')}
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-results"
          aria-activedescendant={filtered.flat[activeIndex] ? `palette-item-${filtered.flat[activeIndex]?.id}` : undefined}
          autoComplete="off"
        />
        {/* Close for pointer and touch (Escape already closes it from the
            keyboard, so it is out of the tab order and Enter stays with the
            results). "Esc" is the key's own glyph; the button has a
            translated name. */}
        <button
          type="button"
          tabIndex={-1}
          onClick={onClose}
          aria-label={t('common.close')}
          aria-keyshortcuts="Escape"
          className="shrink-0 inline-flex items-center justify-center min-w-[44px] min-h-[44px] bg-transparent border-none cursor-pointer text-[var(--ink-3)]"
        >
          <kbd className={`hidden sm:inline-flex ${kbd}`} aria-hidden="true">Esc</kbd>
          <Icons.close size={18} className="sm:hidden" aria-hidden="true" />
        </button>
      </div>

      <div ref={listRef} id="palette-results" role="listbox" className="overflow-y-auto max-h-[min(440px,60dvh)]">
        {/* Nothing found but the Ask row: say so, and asking stays one Enter away. */}
        {filtered.flat.every((i) => i.group === 'ask') && (
          <div className="px-4 pt-6 pb-2 text-center text-ui text-[var(--ink-3)]">{t('palette.noResults')}</div>
        )}
        {filtered.grouped.map(([group, items]) => (
          <div key={group} className="py-1.5" role="group" aria-label={t(`palette.groups.${group}`)}>
            <div className="stat-label px-4 py-1">{t(`palette.groups.${group}`)}</div>
            {items.map((item) => {
              const idx = runningIndex++;
              const isActive = idx === activeIndex;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  id={`palette-item-${item.id}`}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  data-cmd-idx={idx}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => choose(item)}
                  className={[
                    'w-full flex items-center gap-3 px-4 py-2 min-h-[44px] text-start cursor-pointer border-none',
                    isActive ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-text)]' : 'bg-transparent text-[var(--ink-2)]',
                  ].join(' ')}
                >
                  {item.platform ? (
                    <PlatformMark platform={item.platform} size={20} />
                  ) : item.emoji ? (
                    <span className="w-5 text-center" aria-hidden="true">{item.emoji}</span>
                  ) : Icon ? (
                    <Icon size={16} className="shrink-0" aria-hidden="true" />
                  ) : (
                    <span className="w-4" aria-hidden="true" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-body truncate text-[var(--ink)]" dir="auto">{item.label}</span>
                    {item.detail && <span className="block text-meta truncate text-[var(--ink-3)]" dir="auto">{item.detail}</span>}
                  </span>
                  {item.shortcut && <kbd className={kbd}>{item.shortcut}</kbd>}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-2 text-caption text-[var(--ink-3)] border-t border-[var(--line)]">
        <div className="hidden sm:flex items-center gap-3">
          <span><kbd className={kbd}>↑↓</kbd> {t('palette.navigate')}</span>
          <span><kbd className={kbd}>↵</kbd> {t('palette.open')}</span>
          <span><kbd className={kbd}>Esc</kbd> {t('palette.closeHint')}</span>
        </div>
        <div>{t('palette.results', { count: filtered.flat.length })}</div>
      </div>
    </motion.div>
  );
}
